
import { Slab } from "@/types/slab";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Ban, AlertCircle, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PriceCategory } from "@/types/priceCategory";

interface SlabInfoGridProps {
  slab: Slab;
}

export function SlabInfoGrid({ slab }: SlabInfoGridProps) {
  const isLowStock = slab.quantity < 2 && slab.quantity > 0;
  const isOutOfStock = slab.quantity === 0;

  const { data: priceCategory } = useQuery({
    queryKey: ['priceCategory', slab.price_group_id],
    queryFn: async () => {
      if (!slab.price_group_id) return null;

      const { data, error } = await supabase
        .from('cat_price_groups')
        .select('*')
        .eq('id', slab.price_group_id)
        .single();

      if (error) {
        console.error('Erro ao buscar categoria de preço:', error);
        return null;
      }

      return data as PriceCategory;
    },
  });

  const getPriceCategoryDescription = (category: PriceCategory) => {
    if (category.price_type === 'fixed') {
      return `Preço Fixo: ${formatCurrency(category.fixed_price || 0)}`;
    } else {
      return `Faixa: ${formatCurrency(category.min_price || 0)} - ${formatCurrency(category.max_price || 0)}`;
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div className="space-y-1 col-span-2">
        <p className="text-muted-foreground">Preço</p>
        <div className="flex items-center gap-2">
          <p className="font-medium">{formatCurrency(slab.price)}</p>
          {priceCategory && (
            <Badge 
              variant="secondary" 
              className="gap-1 bg-[#F7F2FF] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white cursor-pointer transition-colors"
              title={getPriceCategoryDescription(priceCategory)}
            >
              <DollarSign className="h-3 w-3" />
              {priceCategory.name}
            </Badge>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-muted-foreground">Preço por SQFT</p>
        <p className="font-medium">{formatCurrency(slab.pricePerSqft)}</p>
      </div>

      <div className="space-y-1">
        <p className="text-muted-foreground">SQFT</p>
        <p className="font-medium">{slab.sqft}</p>
      </div>

      <div className="space-y-1">
        <p className="text-muted-foreground">Quantidade</p>
        <div className="flex items-center gap-2">
          <p className={`font-medium ${isOutOfStock ? 'text-red-500 font-bold' : ''}`}>
            {slab.quantity}
          </p>
          {isOutOfStock ? (
            <Badge variant="destructive" className="gap-1">
              <Ban className="h-3 w-3" />
              Out of Stock
            </Badge>
          ) : isLowStock ? (
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              Low Stock
            </Badge>
          ) : null}
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-muted-foreground">Location</p>
        <p className="font-medium">{slab.location}</p>
      </div>

      <div className="space-y-1">
        <p className="text-muted-foreground">Type</p>
        <p className="font-medium">{slab.type}</p>
      </div>

      {slab.dimensions && (
        <div className="space-y-1 col-span-2">
          <p className="text-muted-foreground">Dimensions</p>
          <p className="font-medium">
            {slab.dimensions.length}" × {slab.dimensions.height}"
          </p>
        </div>
      )}

      <div className="space-y-1 col-span-2">
        <p className="text-muted-foreground">Last Updated</p>
        <p className="font-medium">{formatDate(slab.lastUpdated)}</p>
      </div>
    </div>
  );
}
